import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users, Phone, Calendar, Eye, Flame, TrendingUp, BarChart3,
  Send, DollarSign, Target, AlertTriangle, CheckCircle2,
  Info, Clock, ArrowUpRight, Building, Globe, Zap, Mail, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { EmptyDashboardState, PageHint, FirstWinBanner } from '../components/ActivationWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '90d', label: '90 Days' },
];

const INSIGHT_STYLES = {
  success: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/8', border: 'border-emerald-500/20' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/8', border: 'border-amber-500/20' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-500/8', border: 'border-sky-500/20' },
};

function KpiCard({ label, value, icon: Icon, color, suffix = '', prefix = '' }) {
  return (
    <Card className="border-border bg-card hover:bg-card/70 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className={`h-8 w-8 rounded-lg ${color} flex items-center justify-center`}>
            <Icon size={15} className="text-foreground" />
          </div>
        </div>
        <p className="text-xl font-bold text-foreground">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function MiniBar({ label, value, total, color = 'bg-emerald-500' }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-medium text-foreground">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function SimpleBarChart({ data, labelKey, valueKey, color = 'bg-emerald-500', emptyText = 'No data' }) {
  if (!data || data.length === 0) return <p className="text-xs text-muted-foreground py-4 text-center">{emptyText}</p>;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="space-y-2">
      {data.slice(0, 8).map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-20 truncate text-right">{item[labelKey]}</span>
          <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
            <div className={`h-full rounded ${color} transition-all duration-500`}
              style={{ width: `${(item[valueKey] / maxVal) * 100}%` }} />
          </div>
          <span className="text-[10px] text-foreground w-6 text-right">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

function SparkLine({ data, dateKey = 'date', valueKey = 'count', color = '#10b981', height = 48, emptyText = 'No data yet' }) {
  const points = data?.filter(d => d[valueKey] != null && d[valueKey] > 0) || [];
  if (points.length === 0) return <p className="text-[10px] text-muted-foreground py-3 text-center">{emptyText}</p>;

  const values = points.map(d => d[valueKey]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 240;
  const step = w / Math.max(points.length - 1, 1);

  const pathPoints = points.map((d, i) => {
    const x = i * step;
    const y = height - ((d[valueKey] - min) / range) * (height - 8) - 4;
    return `${x},${y}`;
  });

  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" points={pathPoints.join(' ')} />
      {points.length > 0 && (
        <circle cx={pathPoints[pathPoints.length - 1].split(',')[0]} cy={pathPoints[pathPoints.length - 1].split(',')[1]}
          r="2.5" fill={color} />
      )}
    </svg>
  );
}

function PipelineStages({ stages }) {
  if (!stages) return null;
  const stageOrder = ['new', 'contacted', 'qualified', 'showing', 'offer', 'under_contract', 'closed', 'lost'];
  const stageLabels = {
    new: 'New', contacted: 'Contacted', qualified: 'Qualified', showing: 'Showing',
    offer: 'Offer', under_contract: 'Contract', closed: 'Closed', lost: 'Lost',
  };
  const stageColors = {
    new: 'bg-muted-foreground', contacted: 'bg-sky-500', qualified: 'bg-violet-500', showing: 'bg-amber-500',
    offer: 'bg-orange-500', under_contract: 'bg-emerald-500', closed: 'bg-emerald-600', lost: 'bg-red-500',
  };
  const total = stageOrder.reduce((s, k) => s + (stages[k]?.count || 0), 0) || 1;

  return (
    <div className="space-y-2" data-testid="pipeline-stages">
      <div className="flex h-3 rounded-full overflow-hidden bg-muted">
        {stageOrder.map(s => {
          const pct = (stages[s]?.count || 0) / total * 100;
          if (pct === 0) return null;
          return <div key={s} className={`${stageColors[s]} transition-all`} style={{ width: `${pct}%` }} />;
        })}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {stageOrder.filter(s => stages[s]?.count > 0).map(s => (
          <div key={s} className="flex items-center gap-1.5">
            <div className={`h-2 w-2 rounded-full ${stageColors[s]}`} />
            <span className="text-[10px] text-muted-foreground">{stageLabels[s]}</span>
            <span className="text-[10px] font-medium text-foreground">{stages[s]?.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OutcomeDashboardPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [lastReportSent, setLastReportSent] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/outcomes/dashboard?period=${p}`, { headers });
      setData(d);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const sendReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/weekly-email/trigger`, {}, { headers });
      toast.success('Your report is on the way!');
      setReportSent(true);
      setLastReportSent(new Date().toISOString());
      setTimeout(() => setReportSent(false), 15 * 60 * 1000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.info(detail || 'Please wait before sending another report.');
      } else {
        toast.error(detail || 'Failed to send report.');
      }
    } finally {
      setSendingReport(false);
    }
  };

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  useEffect(() => {
    axios.get(`${API}/weekly-email/log?limit=1`, { headers })
      .then(({ data: d }) => {
        if (d.logs?.length) setLastReportSent(d.logs[0].created_at);
      }).catch(() => {});
  }, [token]);

  const k = data?.kpis || {};
  const p = data?.pipeline || {};
  const b = data?.booking_impact || {};
  const cp = data?.call_performance || {};
  const lq = data?.lead_quality || {};
  const charts = data?.charts || {};
  const insights = data?.insights || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5" data-testid="outcome-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Outcomes & Revenue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Your AI's business impact at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={sendReport} disabled={sendingReport || reportSent}
            className={`h-8 text-xs px-3 gap-1.5 ${reportSent
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 cursor-default'
              : 'bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/20'}`}
            data-testid="analytics-send-report-btn">
            {sendingReport ? (
              <><div className="h-3 w-3 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" /> Sending...</>
            ) : reportSent ? (
              <><Check size={12} /> Report Sent</>
            ) : (
              <><Mail size={12} /> Email Me This Week's Report</>
            )}
          </Button>
          {lastReportSent && (
            <span className="text-[10px] text-muted-foreground hidden sm:inline" data-testid="analytics-last-report-sent">
              Last sent {new Date(lastReportSent).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          )}
          {/* Time Filters */}
          <div className="flex items-center gap-1.5 bg-card rounded-lg border border-border p-1" data-testid="time-filters">
            {PERIODS.map(pr => (
              <button key={pr.key} onClick={() => setPeriod(pr.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  period === pr.key
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`filter-${pr.key}`}>
                {pr.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <FirstWinBanner />
      <PageHint id="analytics-intro" text="This dashboard shows the business impact of your AI assistant — leads, bookings, and revenue tracked automatically." type="info" />

      {loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
        </div>
      ) : (k.leads_captured === 0 && k.calls_handled === 0 && k.followups_sent === 0) ? (
        <EmptyDashboardState type="analytics" />
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5" data-testid="kpi-grid">
            <KpiCard label="Leads Captured" value={k.leads_captured} icon={Users} color="bg-sky-500/20" />
            <KpiCard label="Calls Handled" value={k.calls_handled} icon={Phone} color="bg-violet-500/20" />
            <KpiCard label="Appointments" value={k.appointments_booked} icon={Calendar} color="bg-emerald-500/20" />
            <KpiCard label="Showings" value={k.showings_scheduled} icon={Eye} color="bg-amber-500/20" />
            <KpiCard label="Hot Leads" value={k.hot_leads} icon={Flame} color="bg-red-500/20" />
            <KpiCard label="Conversion" value={k.conversion_rate} icon={TrendingUp} color="bg-emerald-500/20" suffix="%" />
            <KpiCard label="Avg Score" value={k.avg_conversion_score} icon={BarChart3} color="bg-violet-500/20" />
            <KpiCard label="Follow-Ups" value={k.followups_sent} icon={Send} color="bg-sky-500/20" />
          </div>

          {/* Pipeline + Insights Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Pipeline Value */}
            <Card className="border-border bg-card lg:col-span-2" data-testid="pipeline-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                    <DollarSign size={14} className="text-emerald-400" /> Pipeline Value
                  </CardTitle>
                  <span className="text-lg font-bold text-emerald-400">${(p.total_pipeline_value || 0).toLocaleString()}</span>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <PipelineStages stages={p.stages} />
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
                  <div className="text-center">
                    <p className="text-lg font-bold text-violet-400">{p.qualified_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Qualified</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-emerald-400">{p.under_contract_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Under Contract</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-400">${(p.closed_value || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">Closed Value</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights */}
            <Card className="border-border bg-card" data-testid="insights-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                  <Zap size={14} className="text-amber-400" /> AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2 max-h-[280px] overflow-y-auto">
                {insights.map((ins, i) => {
                  const st = INSIGHT_STYLES[ins.type] || INSIGHT_STYLES.info;
                  const IcnComp = st.icon;
                  return (
                    <div key={i} className={`flex gap-2 p-2 rounded-lg border ${st.bg} ${st.border}`} data-testid={`insight-${i}`}>
                      <IcnComp size={13} className={`${st.color} shrink-0 mt-0.5`} />
                      <p className="text-[11px] text-foreground leading-relaxed">{ins.text}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Leads by Day */}
            <Card className="border-border bg-card" data-testid="chart-leads-day">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Leads / Day</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SparkLine data={charts.leads_by_day} color="#38bdf8" emptyText="No leads in period" />
              </CardContent>
            </Card>

            {/* Bookings by Day */}
            <Card className="border-border bg-card" data-testid="chart-bookings-day">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Bookings / Day</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SparkLine data={charts.bookings_by_day} color="#10b981" emptyText="No bookings in period" />
              </CardContent>
            </Card>

            {/* Conversion Score Trend */}
            <Card className="border-border bg-card" data-testid="chart-conversion-trend">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Conversion Score Trend</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SparkLine data={charts.conversion_trend} valueKey="score" color="#a78bfa" emptyText="No scores yet" />
              </CardContent>
            </Card>
          </div>

          {/* Booking Impact + Call Performance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Booking Impact */}
            <Card className="border-border bg-card" data-testid="booking-impact-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                  <Calendar size={14} className="text-emerald-400" /> Booking Impact
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2.5">
                <MiniBar label="Booking Attempts" value={b.booking_attempts} total={Math.max(b.booking_attempts, 1)} color="bg-sky-500" />
                <MiniBar label="Successful Bookings" value={b.successful_bookings} total={Math.max(b.booking_attempts, 1)} color="bg-emerald-500" />
                <MiniBar label="Missed Opportunities" value={b.missed_opportunities} total={Math.max(b.booking_attempts, 1)} color="bg-red-500" />
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                  <div className="text-center"><p className="text-sm font-bold text-sky-400">{b.buyer_consults_booked}</p><p className="text-[9px] text-muted-foreground">Buyer Consults</p></div>
                  <div className="text-center"><p className="text-sm font-bold text-violet-400">{b.seller_consults_booked}</p><p className="text-[9px] text-muted-foreground">Seller Consults</p></div>
                  <div className="text-center"><p className="text-sm font-bold text-amber-400">{b.showing_requests_booked}</p><p className="text-[9px] text-muted-foreground">Showings</p></div>
                </div>
              </CardContent>
            </Card>

            {/* Call Performance */}
            <Card className="border-border bg-card" data-testid="call-performance-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                  <Phone size={14} className="text-violet-400" /> Call Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-muted-foreground">Avg Duration</p><p className="text-sm font-bold text-foreground">{cp.avg_duration_seconds ? `${Math.round(cp.avg_duration_seconds / 60)}m ${cp.avg_duration_seconds % 60}s` : '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Avg Score</p><p className="text-sm font-bold text-foreground">{cp.avg_conversion_score || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Objection Rate</p><p className="text-sm font-bold text-amber-400">{cp.objection_rate}%</p></div>
                  <div><p className="text-xs text-muted-foreground">Escalation Rate</p><p className="text-sm font-bold text-red-400">{cp.escalation_rate}%</p></div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                  <div><p className="text-xs text-muted-foreground">Ready-to-Book</p><p className="text-sm font-bold text-emerald-400">{cp.ready_to_book_detections}</p></div>
                  <div><p className="text-xs text-muted-foreground">Booked After AI</p><p className="text-sm font-bold text-emerald-400">{cp.booked_after_ai_call}</p></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Lead Quality + Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Lead Quality */}
            <Card className="border-border bg-card" data-testid="lead-quality-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                  <Target size={14} className="text-amber-400" /> Lead Quality
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {/* Temperature */}
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">Hot {lq.temperature?.hot || 0}</Badge>
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">Warm {lq.temperature?.warm || 0}</Badge>
                  <Badge className="bg-secondary text-muted-foreground border-border text-[10px]">Cold {lq.temperature?.cold || 0}</Badge>
                </div>
                {/* Buyer/Seller */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <ArrowUpRight size={11} className="text-sky-400" />
                    <span className="text-[11px] text-muted-foreground">Buyers</span>
                    <span className="text-[11px] font-medium text-foreground">{lq.buyer_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Building size={11} className="text-violet-400" />
                    <span className="text-[11px] text-muted-foreground">Sellers</span>
                    <span className="text-[11px] font-medium text-foreground">{lq.seller_count || 0}</span>
                  </div>
                </div>
                {/* Language */}
                <div className="flex items-center gap-2">
                  <Globe size={11} className="text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">EN: {lq.english_leads || 0} | ES: {lq.spanish_leads || 0}</span>
                </div>
                {/* Top Areas */}
                {lq.top_areas?.length > 0 && (
                  <div className="space-y-1 pt-1 border-t border-border">
                    <p className="text-[10px] text-muted-foreground uppercase">Top Areas</p>
                    {lq.top_areas.map((a, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-[11px] text-foreground truncate">{a.area}</span>
                        <span className="text-[11px] font-medium text-foreground">{a.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pipeline Distribution */}
            <Card className="border-border bg-card" data-testid="chart-pipeline-dist">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Pipeline Distribution</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <SimpleBarChart data={charts.pipeline_distribution} labelKey="stage" valueKey="count" color="bg-violet-500" emptyText="No leads in pipeline" />
              </CardContent>
            </Card>

            {/* Intent Breakdown */}
            <Card className="border-border bg-card" data-testid="chart-intent-breakdown">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Call Intent Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <SimpleBarChart data={charts.intent_breakdown} labelKey="intent" valueKey="count" color="bg-sky-500" emptyText="No call intents recorded" />
              </CardContent>
            </Card>
          </div>

          {/* Budget Ranges */}
          {lq.budget_ranges && Object.values(lq.budget_ranges).some(v => v > 0) && (
            <Card className="border-border bg-card" data-testid="budget-ranges-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-foreground text-xs uppercase tracking-wider">Lead Budget Distribution</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { key: 'under_200k', label: '<$200K' },
                    { key: '200k_400k', label: '$200-400K' },
                    { key: '400k_600k', label: '$400-600K' },
                    { key: '600k_800k', label: '$600-800K' },
                    { key: '800k_1m', label: '$800K-1M' },
                    { key: 'over_1m', label: '$1M+' },
                  ].map(({ key, label }) => (
                    <div key={key} className="text-center p-2 rounded-lg bg-muted border border-border/30">
                      <p className="text-sm font-bold text-foreground">{lq.budget_ranges?.[key] || 0}</p>
                      <p className="text-[9px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
